/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { OPTIMIZE_FOR, ROAD_SIDE } from 'types/modules/routing/routeOptimization'

import type { OptimizationJob, OptimizerCapacity, OptimizerSettings } from 'types/modules/routing/routeOptimization'

// The job's status and result are server state, so they stay in the RTKQ cache rather than here.
interface OptimizationState {
  drawerOpen: boolean
  targetGridId: string | null
  settings: OptimizerSettings
  capacity: OptimizerCapacity
  job: OptimizationJob | null
}

const DEFAULT_SETTINGS: OptimizerSettings = {
  optimizeFor: OPTIMIZE_FOR.TIME,
  avoidTolls: false,
  avoidHighways: false,
  roadSide: ROAD_SIDE.SAME_SIDE,
  avoidUturns: false,
  avoidLeftTurns: false,
  avoidRightTurns: false,
  avoidSharpTurns: false,
  avoidServiceRoads: false,
  useTruckProfile: false,
  truckHeightFt: '',
  truckWidthFt: '',
  truckLengthFt: '',
  truckWeightLb: '',
}

const initialState: OptimizationState = {
  drawerOpen: false,
  targetGridId: null,
  settings: DEFAULT_SETTINGS,
  capacity: { truckName: 'Truck 12', load: 9.4, capacityValue: 12, capacityUnit: 'tons' },
  job: null,
}

export const OPTIMIZATION_KEY = 'routeOptimization' as const

const optimizationSlice = createSlice({
  name: OPTIMIZATION_KEY,
  initialState,
  reducers: {
    openOptimizer: (state, action: PayloadAction<string>) => {
      state.drawerOpen = true
      state.targetGridId = action.payload
    },
    closeOptimizer: state => {
      state.drawerOpen = false
    },
    setOptimizerSettings: (state, action: PayloadAction<OptimizerSettings>) => {
      state.settings = action.payload
    },
    startOptimization: (state, action: PayloadAction<OptimizationJob>) => {
      state.job = action.payload
      state.drawerOpen = false
    },
    finishOptimization: state => {
      state.job = null
    },
    cancelOptimization: state => {
      state.job = null
    },
  },
})

export const { openOptimizer, closeOptimizer, setOptimizerSettings, startOptimization, finishOptimization, cancelOptimization } =
  optimizationSlice.actions

// Narrow root-state shape so consumers and tests don't need the full RootState.
export interface OptimizationRootStateSlice {
  [OPTIMIZATION_KEY]?: OptimizationState
}

const selectState = (state: OptimizationRootStateSlice): OptimizationState => state[OPTIMIZATION_KEY] ?? initialState

export const selectIsOptimizerOpen = (state: OptimizationRootStateSlice): boolean => selectState(state).drawerOpen

export const selectOptimizerTargetGridId = (state: OptimizationRootStateSlice): string | null => selectState(state).targetGridId

export const selectOptimizerSettings = (state: OptimizationRootStateSlice): OptimizerSettings => selectState(state).settings

export const selectOptimizerCapacity = (state: OptimizationRootStateSlice): OptimizerCapacity => selectState(state).capacity

export const selectOptimizationJob = (state: OptimizationRootStateSlice): OptimizationJob | null => selectState(state).job

export default optimizationSlice.reducer
